import type { CaptureCamera, PrinterSummary } from './model';

/**
 * What a calibration print carries, so a survey's counts read against it.
 *
 * Eight marker stickers (`survey_from_photo(expected_markers=8)`) and sixteen
 * crosses (`pdfout.default_marks()`: a 5 x 3 grid and one that fixes the
 * orientation). `web/test/logic.ts` reads both out of the solver, so a change
 * there fails the checks rather than quietly making these wrong.
 */
export const EXPECTED_MARKERS = 8;
export const EXPECTED_CROSSES = 16;

/** "7 / 8": a short count has to look short. */
export function countAgainst(found: number, expected: number): string {
  return `${found} / ${expected}`;
}

/**
 * Can a sheet on this printer be processed at all?
 *
 * Both halves are required: with no bar length every sheet is refused before
 * any work, and with no survey there is nothing tying the bed to the page.
 */
export function printerReady(p: PrinterSummary): boolean {
  return p.calibrated && Boolean(p.surveyedAt);
}

/** Ready printers first, each half in the order the server listed them. */
export function orderPrinters(printers: PrinterSummary[]): PrinterSummary[] {
  return [...printers.filter(printerReady),
          ...printers.filter((p) => !printerReady(p))];
}

/**
 * Which capture panels the job page shows for a printer's camera setting.
 *
 * Only the one the printer is set up for, so the operator is not offered a
 * camera whose photographs the survey was not made through. Unset, or a
 * printer that could not be loaded, shows both: hiding the only working route
 * would be worse than showing one too many.
 */
export function captureOptions(camera: CaptureCamera | null | undefined) {
  return { usb: camera !== 'phone', phone: camera !== 'usb' };
}

/** How each setting reads in the Printers screen's dropdown. */
export const CAMERA_LABELS: Record<CaptureCamera, string> = {
  usb: 'USB camera on the lid (workstation)',
  phone: 'Phone, by QR code',
};

/**
 * The printer the screens start on: the MO-180 with the camera on its lid, the
 * one in use since the fixed-focus camera went in (2026-10). It need not exist
 * on every stage, and both screens fall back when it does not.
 */
export const PREFERRED_PRINTER = 'mo-180-fixed-camera';

/**
 * The preferred printer if it is ready, else the first ready printer, else the
 * first printer, else the seed id. Only a ready one is preferred: a job made
 * on a printer that refuses every sheet is worse than one on another printer.
 */
export function defaultPrinterId(printers: PrinterSummary[]): string {
  const preferred = printers.find((p) => p.printerId === PREFERRED_PRINTER);
  if (preferred && printerReady(preferred)) return preferred.printerId;
  return orderPrinters(printers)[0]?.printerId ?? 'default';
}

/** The dropdown label, saying why a printer would refuse every sheet. */
export function printerOptionLabel(p: PrinterSummary): string {
  if (!p.calibrated) return `${p.name} — no bar length`;
  if (!p.surveyedAt) return `${p.name} — not yet surveyed`;
  return p.name;
}

/**
 * Whether the jobs screen warns that nothing can be printed.
 *
 * Only when no printer at all is surveyed, including when there are none. One
 * unsurveyed printer beside a ready one is not a blocker, and the dropdown
 * already labels it.
 */
export function showSurveyWarning(printers: PrinterSummary[]): boolean {
  return !printers.some((p) => p.surveyedAt);
}

/** Heading for the movement panel, naming the survey it is measured from. */
export function movementHeading(previousCreated: string | null | undefined): string {
  const base = 'Movement since last survey';
  return previousCreated
    ? `${base}, ${new Date(previousCreated).toLocaleString()}`
    : base;
}

/**
 * Has a bar moved, rather than two surveys disagreeing within their noise?
 *
 * The solver decides the threshold from the two surveys' residuals and records
 * it, so the screens and the survey's own note cannot disagree.
 */
export function barMoved(moved: { worstMil: number; thresholdMil: number }): boolean {
  return moved.worstMil > moved.thresholdMil;
}

/**
 * What each calibration photograph should look like, in shot order.
 *
 * One shot since the lens is measured on its own (`LENS_SHOTS`); four framed
 * differently were asked for briefly on 2026-10-02. The count must match the
 * API's `CALIBRATION_PHOTOS`; `web/test/logic.ts` checks it.
 */
export const CALIBRATION_SHOTS: readonly string[] = [
  'The same framing as a production photo: square to the bed, both bars in.',
];

/**
 * The ChArUco board photographs, in shot order (2026-10-02).
 *
 * Between them the board has to reach every part of the frame -- the edges
 * and corners above all, where the lens bends most and where the bars sit --
 * and some have to be tilted, or the focal length cannot be told from the
 * distance. "Fill the screen" was not enough: the first real set kept every
 * board corner 200 px in from the edges. Nor is moving closer: the second set's
 * close-ups fitted no lens model at all, because the phone's lens changes as it
 * focuses in. So every shot is from the height sheets are photographed from,
 * and the phone moves *sideways* to put the board over each corner and edge. The
 * solver refuses a set that leaves any frame corner empty, and names it. The
 * count must match the API's `LENS_PHOTOS`; `web/test/logic.ts` checks it.
 */
export const LENS_SHOTS: readonly string[] = [
  'Whole board in frame, phone upright, from straight above.',
  'Whole board in frame, phone sideways, from straight above.',
  'Phone upright, same height: move sideways until the board covers the top-left corner of the screen and runs off both edges there.',
  'Phone upright, same height: the board covers the top-right corner of the screen, running off both edges.',
  'Phone upright, same height: the board covers the bottom-left corner of the screen, running off both edges.',
  'Phone upright, same height: the board covers the bottom-right corner of the screen, running off both edges.',
  'Phone sideways, same height, tilted a little: the board runs off the left edge of the screen.',
  'Phone sideways, same height, tilted a little: the board runs off the right edge of the screen.',
  'Phone sideways, same height, tilted a little: the board runs off the top edge of the screen.',
  'Phone sideways, same height, tilted a little: the board runs off the bottom edge of the screen.',
  'Phone upright, same height, tilted -- shoot from beyond the front edge of the bed.',
  'Phone sideways, same height, tilted -- shoot from beyond the side of the bed.',
];

/**
 * The same twelve, for the camera on the printer's lid (2026-10-05).
 *
 * That camera cannot move, so the board does, flat on the bed with the vacuum
 * holding it -- slid with the vacuum off, shot with it on. It cannot reach the
 * corners of the picture -- the lid camera sees the machine's sides and the
 * floor round the bed -- and does not need to: the solver measures this camera
 * over the bed, located by the bar markers, so the shots cover each bar
 * instead (2026-10-08). "Preview" is the sensor frame: nothing rotates a fixed
 * camera's picture.
 *
 * No tilted shots. They are only there to find the focal length, which a
 * camera that always sees the same flat bed from the same place does not
 * need: its correction only has to straighten that one plane, and flat shots
 * of a board on it measure exactly that. Propped up off the vacuum the board
 * bowed 1-2 mm (2026-10-08), and those six shots took the held-out fit from
 * 1.16 px to 1.82 px against a 1.5 px limit. `web/test/logic.ts` checks every
 * marker position has a shot and that none is propped.
 */
export const USB_LENS_SHOTS: readonly string[] = [
  'Board flat on the bed, centred under the camera, whole board in the preview.',
  'Board flat, turned a quarter turn, centred, whole board in the preview.',
  'Slide the board until it covers the top-left marker in the preview, bar and all. Push it well past the marker.',
  'The board covers the top-middle markers in the preview, bar and all.',
  'The board covers the top-right marker in the preview, bar and all -- as far as the print head allows.',
  'The board covers the bottom-left marker in the preview, bar and all. Push it well past the marker.',
  'The board covers the bottom-middle markers in the preview, bar and all.',
  'The board covers the bottom-right marker in the preview, bar and all -- as far as the print head allows.',
  'The board over the left end of the bed, halfway between the bars.',
  'The board over the right end of the bed, halfway between the bars.',
  'Turned a quarter turn, the board against the top bar, centred.',
  'Turned a quarter turn, the board against the bottom bar, centred.',
];

/** The phone page for the lens photos, from the calibration QR's link. */
export function lensCaptureUrl(calibrationUrl: string): string {
  const url = new URL(calibrationUrl);
  url.pathname = url.pathname.replace(/\/?$/, '/lens');
  return url.toString();
}
